// Print/export-PDF builder for QuoteView (iter-102 refactor).
//
// Extracted from QuoteView.jsx's inline handlePrint method. The
// component now just collects its current state into a payload and
// calls `buildAndPrintQuote(payload)`. Keeping this in its own
// module makes the printable HTML / CSS easier to iterate on without
// touching React render code, and shrinks QuoteView's footprint by
// ~220 lines.
//
// Side effects: opens a new browser window, writes HTML, calls print.
// All popup-blocker handling lives here.

import { toast } from "sonner";
import { pdfjs } from "react-pdf";
import { fetchQuoteAttachmentData } from "../../lib/api";

const escHtml = (s) =>
    String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

// iter-110 (bugfix): defensive markdown-emphasis strip so `**bold**` /
// `__bold__` never leak into the printed PDF regardless of the DB
// state or whether the retroactive-strip useEffect fired.
const stripMd = (s) =>
    String(s ?? "")
        .replace(/\*\*(.+?)\*\*/g, "$1")
        .replace(/__(.+?)__/g, "$1")
        // Trailing/leading unclosed markers on names Claude sometimes
        // truncates (e.g. `**Hydrostatic Testing` with no closer).
        .replace(/^\*\*+/, "")
        .replace(/\*\*+$/, "");

const cleanHtml = (s) => escHtml(stripMd(s));

const fmtMoney = (n) =>
    `$${(Number(n) || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

/**
 * Build and trigger the print/export-PDF flow.
 *
 * @param {object} p
 * @param {string} p.companyName
 * @param {string} p.addr1
 * @param {string} p.addr2
 * @param {string} p.addr3
 * @param {string} p.companyPhone
 * @param {string} p.companyEmail
 * @param {string} p.preparedBy
 * @param {string} p.logoDataUrl
 * @param {string} p.custName
 * @param {string} p.custAddr
 * @param {string} p.custCity
 * @param {string} p.custPhone
 * @param {string} p.custEmail
 * @param {string} p.date
 * @param {string} p.validDays
 * @param {string} p.quoteId
 * @param {string} p.taxLabel
 * @param {Array}  p.lines               full line set (hidden filtered inside)
 * @param {Array}  p.attachments         [{name, kind: 'image'|'pdf', storage_path}]
 * @param {boolean} p.itemized            when false the per-line amount column is omitted
 * @param {number} p.profitMultiplier
 * @param {number} p.baseWithProfit
 * @param {number} p.tax
 * @param {number} p.grand
 * @param {number} p.taxRate               tax rate as decimal (0.13 = 13%)
 */
export async function buildAndPrintQuote(p) {
    const {
        companyName, addr1, addr2, addr3, companyPhone, companyEmail,
        preparedBy, logoDataUrl,
        custName, custAddr, custCity, custPhone, custEmail,
        date, validDays, quoteId, taxLabel,
        lines, attachments, itemized, profitMultiplier,
        baseWithProfit, tax, grand, taxRate,
    } = p;

    // Open the popup synchronously so popup blockers don't kill us
    // while we wait on attachment rendering. Loader gets replaced
    // once everything's ready.
    const popup = window.open("", "_blank", "width=900,height=1100");
    if (!popup) {
        toast.error("Popup blocked — allow popups to print");
        return;
    }
    popup.document.open();
    popup.document.write(
        `<!doctype html><meta charset="utf-8"/><title>Preparing quote…</title>`
        + `<body style="font-family:-apple-system,Segoe UI,Helvetica,sans-serif;`
        + `display:flex;align-items:center;justify-content:center;height:100vh;`
        + `margin:0;color:#555">Preparing quote… please wait.</body>`,
    );
    popup.document.close();

    const addressLine = [addr1, addr2].filter(Boolean).join(" · ");
    const extraAddr = addr3 ? `<div>${escHtml(addr3)}</div>` : "";
    const prepared = preparedBy
        ? `<div>Prepared by: ${escHtml(preparedBy)}</div>`
        : "";
    const cemail = companyEmail ? `<div>${escHtml(companyEmail)}</div>` : "";
    const cphone = companyPhone ? `<div>${escHtml(companyPhone)}</div>` : "";
    const custCityLine = custCity ? `<div>${escHtml(custCity)}</div>` : "";
    const custPhoneLine = custPhone ? `<div>${escHtml(custPhone)}</div>` : "";
    const custEmailLine = custEmail ? `<div>${escHtml(custEmail)}</div>` : "";

    const lineRows = (lines || [])
        .filter((ln) => !ln.hidden)
        .map((ln, i) => {
            const desc = ln.name || ln.description || "";
            const notes = ln.notes
                ? `<div class="notes">${cleanHtml(ln.notes)}</div>`
                : "";
            // Itemize OFF → omit the Amount cell so the customer
            // never sees per-line prices (matches the on-screen
            // behaviour of the toggle).
            const amtCell = itemized
                ? `<td class="amt">${fmtMoney(
                      (Number(ln.line_total) || 0) * profitMultiplier,
                  )}</td>`
                : "";
            return `<tr><td class="num">${i + 1}</td><td>${cleanHtml(
                desc,
            )}${notes}</td>${amtCell}</tr>`;
        })
        .join("");

    const logoImg = logoDataUrl
        ? `<img src="${logoDataUrl}" alt="Logo" />`
        : "";

    // Render extra-page attachments (images + PDF pages flattened to
    // images via pdfjs). Failures on a single attachment shouldn't
    // stop the rest of the quote from printing.
    let attachmentPages = "";
    for (const a of (attachments || [])) {
        try {
            const { data_url } = await fetchQuoteAttachmentData(a.storage_path);
            if (a.kind === "image") {
                attachmentPages += (
                    `<div class="attach-page"><img src="${data_url}" alt="${escHtml(a.name)}" /></div>`
                );
            } else if (a.kind === "pdf") {
                const b64 = data_url.split(",")[1] || "";
                const bin = atob(b64);
                const bytes = new Uint8Array(bin.length);
                for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
                const pdfDoc = await pdfjs.getDocument({ data: bytes }).promise;
                for (let pg = 1; pg <= pdfDoc.numPages; pg++) {
                    const page = await pdfDoc.getPage(pg);
                    const viewport = page.getViewport({ scale: 1.5 });
                    const canvas = document.createElement("canvas");
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    await page.render({
                        canvasContext: canvas.getContext("2d"),
                        viewport,
                    }).promise;
                    const imgUrl = canvas.toDataURL("image/jpeg", 0.85);
                    attachmentPages += (
                        `<div class="attach-page"><img src="${imgUrl}" alt="${escHtml(a.name)} p${pg}" /></div>`
                    );
                }
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("attachment render failed", a, err);
            toast.error(`Couldn't include "${a.name}" — skipped.`);
        }
    }

    // ---- iter-110: Steeltooth Contracting terms & conditions + payment
    // schedule + signature pages. Rendered as page 2/3/4 immediately
    // after the main quote body, before any customer-uploaded
    // attachments. Deposit / final / tax figures auto-calculate from
    // the current baseWithProfit + taxRate.
    const depositBase = baseWithProfit / 2;
    const finalBase = baseWithProfit / 2;
    const grandTaxAmt = tax;
    const grandTotalAmt = grand;

    const termsPage2Items = [
        "Work is to be continuous and completed in ONE mobilization(s) unless otherwise noted in scope: Additional mobilization is $1,850.00. Any delays or work stoppages in the work described above, caused by no fault of STEELTOOTH CONTRACTING, will be back charged at a rate of: $500.00/crew hour (partial hour delays will be accumulated daily). All payments must be paid in full to validate warranty.",
        "All quantities are estimates only, final invoice will be based on actual measured quantities.",
        "Under no circumstances shall anyone other than our firm be entitled to use our construction equipment or rentals.",
        "Prices valid for the 2026 construction season and open to acceptance for 15 days from the Bid Date, subject to the notes below.",
        "STEELTOOTH CONTRACTING is not responsible for any rock/cobble/shale excavation, additional costs will apply.",
        "Any/all material to be hauled off site/testing/loading/hauling and disposal fees are extra unless otherwise identified.",
        "All excavated material to meet Table 1, any exceedance will be charged extra.",
        "All haulage to be completed prior to March 1st or after April 30th if designated a load restricted road otherwise additional charges will apply.",
        "No allowances have been made for shoring/hording/fencing/ protection of excavation and disposal, to be completed on T&M.",
        "Any work within 1m of underground utilities must be completed using hydrovac excavation and will be billed EXTRA in accordance to added scope.",
        "All material testing and compaction testing (QA/QC) by others.",
        "Dust control/ street sweeping done by others.",
        "Field office, portable toilets and garbage bins provided by others.",
        "Clear and unobstructed access to the site is to be provided by the owner during normal working hours of 7am-4pm M-F.",
        "Traffic control/ traffic protection/flagging/signage/barriers by others.",
        "All fees/ permits, road closures, road housekeeping, air rights, etc., by others.",
        "Private locates and Pre-condition survey by others.",
        "Bonding not included. Wrap-up/ builder risk liability insurance not included.",
        "Payment terms: 50% Deposit, 50% due upon completion.",
        "Any work not shown as a scope, drawings or agreed upon before commencing of work.",
        "STEELTOOTH CONTRACTING will not be responsible for delays in completion of work arising from strikes, walkouts, lockouts, material shortages, equipment breakdowns, fires, any acts of God or any other causes beyond the control of STEELTOOTH CONTRACTING.",
    ];
    const termsPage3Items = [
        "Any winter housekeeping such as: cleaning snow, frost protection, heating footings, breaking frost, defrost ice or dirt etc, unless listed above on scope of work.",
        "STEELTOOTH CONTRACTING will not be responsible for damage caused by mobilization of heavy equipment to complete scope of work.",
        "No Asbestos, Lead, Mercury or other hazardous substances abatement.",
        "Electrical wiring to be done by others.",
        "Any dewatering required is not included in this scope of work, can be provided on a time and material basis unless otherwise specified.",
        "All funds in Canadian dollars and applicable taxes are rated at 13%.",
        "All material remains the property of STEELTOOTH CONTRACTING until final invoice has been paid.",
        "No allowances have been made to secure tanks to the ground in the event of excessive groundwater and upward pressure.",
        "Tree removal by others, stump disposal extra unless otherwise noted.",
        "Final grading, additional sod/seed and additional topsoil is not included and extra.",
        "Plumber is responsible to connect and seal sewer pipe from house into the septic tank as per plumbing regulations.",
        "Annual maintenance contract must be signed with tertiary unit manufacturer.",
        "Anything additional requested by the Inspection Authority or Township, not included in the drawings is billed at a time and material basis.",
    ];

    const buildTermsList = (items) =>
        `<ul class="terms-list">${items
            .map((t) => `<li>${escHtml(t)}</li>`)
            .join("")}</ul>`;

    const initialsBlock = `
        <div class="initials-row">
            <span class="initials-label">Initial Here:</span>
            <span class="initials-circle"></span>
            <span class="initials-circle"></span>
        </div>`;

    const termsPagesHtml = `
        <div class="terms-page">
            <div class="terms-hdr">
                ${logoImg ? `<div class="terms-logo">${logoImg}</div>` : ""}
                <div class="terms-brand">STEELTOOTH <span>&mdash; CONTRACTING &mdash;</span></div>
            </div>
            <h2 class="terms-h1">TERMS AND CONDITIONS</h2>
            ${buildTermsList(termsPage2Items)}
            ${initialsBlock}
            <div class="terms-pageno">Page 2 of 4</div>
        </div>
        <div class="terms-page">
            <div class="terms-hdr">
                ${logoImg ? `<div class="terms-logo">${logoImg}</div>` : ""}
                <div class="terms-brand">STEELTOOTH <span>&mdash; CONTRACTING &mdash;</span></div>
            </div>
            <h2 class="terms-h1">TERMS AND CONDITIONS</h2>
            ${buildTermsList(termsPage3Items)}
            <p class="terms-emph">*** Client agrees to pay 2836478 ONTARIO INC the below amounts in schedule ***</p>
            <div class="payment-box">
                <div class="payment-title">PAYMENT</div>
                <div class="payment-sub">SCHEDULE A</div>
                <div class="payment-row"><span>DEPOSIT</span><span>${fmtMoney(depositBase)} <em>+TAX</em></span></div>
                <div class="payment-row"><span>FINAL</span><span>${fmtMoney(finalBase)} <em>+TAX</em></span></div>
                <div class="payment-row"><span>TOTAL</span><span>${fmtMoney(baseWithProfit)}</span></div>
                <div class="payment-row"><span>TAX</span><span>${fmtMoney(grandTaxAmt)}</span></div>
                <div class="payment-row grand"><span>Total Amount:</span><span>${fmtMoney(grandTotalAmt)}</span></div>
            </div>
            <p class="terms-fine">Invoiced prior to work completed and materials supplied. Payment upon completion of work and/or materials. No Holdback. An interest charge of 2% per month (24% annually) will be assessed to overdue accounts. Nonpayment could be considered grounds for us to stop work, delay project and/or termination of contract.</p>
            ${initialsBlock}
            <div class="terms-pageno">Page 3 of 4</div>
        </div>
        <div class="terms-page">
            <div class="terms-hdr">
                ${logoImg ? `<div class="terms-logo">${logoImg}</div>` : ""}
                <div class="terms-brand">STEELTOOTH <span>&mdash; CONTRACTING &mdash;</span></div>
            </div>
            <div class="sig-block">
                <div class="sig-x">X</div>
                <div class="sig-role">Purchaser</div>
                <div class="sig-lines">
                    <div class="sig-line"><span class="sig-lbl">Name:</span><span class="sig-fill"></span></div>
                    <div class="sig-line"><span class="sig-lbl">Title:</span><span class="sig-fill"></span></div>
                    <div class="sig-line"><span class="sig-lbl">Date:</span><span class="sig-fill"></span></div>
                    <div class="sig-authority">(I have authority to bind the Company)</div>
                </div>
            </div>
            <div class="sig-block">
                <div class="sig-x">X</div>
                <div class="sig-role">Witness</div>
                <div class="sig-lines">
                    <div class="sig-line"><span class="sig-lbl">Name:</span><span class="sig-fill"></span></div>
                    <div class="sig-line"><span class="sig-lbl">Title:</span><span class="sig-fill"></span></div>
                    <div class="sig-line"><span class="sig-lbl">Date:</span><span class="sig-fill"></span></div>
                    <div class="sig-authority">(I have authority to bind the Company)</div>
                </div>
            </div>
            <div class="sig-block">
                <div class="sig-x">X</div>
                <div class="sig-role">2836478 ONTARIO INC · Corey Watman &mdash; President</div>
                <div class="sig-lines">
                    <div class="sig-line"><span class="sig-lbl">Date:</span><span class="sig-fill"></span></div>
                    <div class="sig-authority">(I have authority to bind the Company)</div>
                </div>
            </div>
            <p class="terms-fine">This quotation becomes effective as a contract when signed by duly authorized representative of each party. Any changes to this quotation must, however, be signed by a representative from 2836478 ONTARIO INC. Instructions in writing or verbally will serve as an irrevocable agreement from Owner or General Contractor, to proceed with the contract work.</p>
            <p class="terms-fine">Please sign and return to email: <strong>INFO@STEELTOOTH.CA</strong> Or Call <strong>705-333-4376</strong></p>
            <p class="terms-expiry">"This quote expires in 30 days from the above listed date."</p>
            <div class="terms-pageno">Page 4 of 4</div>
        </div>
    `;

    const html = `<!doctype html><html><head><meta charset="utf-8"/>
<title>Quote ${escHtml(quoteId || "")}</title>
<style>
  html,body{margin:0;padding:0;background:#fff;color:#111}
  body{font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;padding:0.5in}
  .pf-quote{max-width:7.5in;margin:0 auto;font-size:11pt}
  /* Condensed header — logo + company left, meta top-right. */
  .hdr{display:grid;grid-template-columns:auto 1fr auto;gap:18px;align-items:start;margin-bottom:20px;padding-bottom:14px;border-bottom:1px solid #ddd}
  .logo{width:140px}
  .logo img{display:block;max-width:140px;max-height:90px;width:auto;height:auto;object-fit:contain}
  .company{font-size:10.5pt;line-height:1.45}
  .company .name{font-weight:700;font-size:12pt;margin-bottom:2px}
  .meta{font-size:9.5pt;min-width:200px}
  .meta .row{display:flex;justify-content:flex-end;gap:8px;margin-bottom:4px;align-items:center}
  .meta .lbl{font-weight:700;text-transform:uppercase;letter-spacing:.04em;font-size:9pt;color:#333}
  .meta .val{border:1px solid #222;padding:3px 8px;min-width:96px;text-align:center;font-size:10pt;background:#fafafa}
  /* Customer */
  .cust{display:grid;grid-template-columns:1fr 1fr;gap:18px;margin-bottom:14px;font-size:10.5pt}
  .cust-head{background:#1e6fd9;color:#fff;font-weight:700;padding:5px 10px;margin-bottom:4px;text-transform:uppercase;letter-spacing:.04em;font-size:9.5pt}
  .cust .name{font-weight:700;margin-bottom:1px}
  /* Line items */
  table.lines{width:100%;border-collapse:collapse;margin-top:6px;font-size:10pt}
  table.lines th{text-align:left;padding:7px 10px;border-bottom:2px solid #111;font-size:9pt;text-transform:uppercase;letter-spacing:.04em}
  table.lines th.right,table.lines td.amt{text-align:right}
  table.lines td{padding:8px 10px;border-bottom:1px solid #e6e6e6;vertical-align:top}
  table.lines td.num{width:36px;color:#555}
  table.lines td.amt{width:110px;white-space:nowrap;font-weight:600}
  table.lines .notes{font-size:9pt;color:#666;margin-top:2px}
  .totals{margin-top:6px}
  .totals .trow{display:flex;justify-content:space-between;padding:7px 10px;border-bottom:1px solid #e6e6e6;font-size:10pt}
  .totals .trow.grand{border-bottom:none;font-weight:800;font-size:12pt;background:#f4f6fa;margin-top:4px}
  .totals .lbl{font-weight:700;text-transform:uppercase;letter-spacing:.04em;font-size:9pt}
  .totals .tax-lbl{font-size:9pt;color:#555;font-weight:400;margin-left:8px;text-transform:none;letter-spacing:0}
  /* Appended attachment pages — one per sheet, image scaled to fit. */
  .attach-page{break-before:page;page-break-before:always;display:flex;align-items:center;justify-content:center;padding:0;margin:0}
  .attach-page img{max-width:7.5in;max-height:9.8in;width:auto;height:auto;object-fit:contain;display:block}
  /* iter-110 (bugfix, second pass): the previous fix removed
     min-height but blanks still appeared. Culprits: (a) padding on
     .terms-page stacked with @page margin, pushing content past the
     10.2in usable area of Letter, and (b) page-break-inside:avoid
     which per CSS spec forces a page break BEFORE the element when
     content overflows, creating phantom blank pages. Fix: kill both,
     rely purely on break-before:page. Font size + list padding are
     already tight enough for each section to fit one page. */
  @page{size:letter;margin:0.4in}
  @media print{body{padding:0;margin:0}}
  html,body{margin:0;padding:0}
  /* iter-110 — Steeltooth terms/payment/signature pages */
  .terms-page{break-before:page;page-break-before:always;padding:0;color:#111;font-size:10pt;line-height:1.4}
  .terms-page:last-of-type{break-after:auto;page-break-after:auto}
  .terms-hdr{display:flex;align-items:center;gap:14px;border-bottom:2px solid #1e6fd9;padding-bottom:8px;margin-bottom:14px}
  .terms-logo img{max-width:80px;max-height:60px;object-fit:contain}
  .terms-brand{color:#1e6fd9;font-weight:800;font-size:16pt;letter-spacing:.06em}
  .terms-brand span{font-size:10pt;font-weight:400;letter-spacing:.16em;display:block;margin-top:-2px}
  .terms-h1{font-size:14pt;font-weight:800;color:#1e6fd9;text-align:center;letter-spacing:.08em;margin:6px 0 10px}
  .terms-list{list-style:none;padding:0;margin:0}
  .terms-list li{padding:2px 0 2px 14px;position:relative;font-size:9pt;line-height:1.35}
  .terms-list li:before{content:"–";position:absolute;left:0;color:#1e6fd9;font-weight:700}
  .terms-emph{text-align:center;font-style:italic;font-weight:700;color:#1e6fd9;margin:12px 0 6px;font-size:10pt}
  .payment-box{border:2px solid #1e6fd9;padding:10px 14px;margin:8px 0}
  .payment-title{font-weight:800;color:#1e6fd9;letter-spacing:.08em;font-size:12pt}
  .payment-sub{font-weight:600;color:#666;font-size:9pt;margin-bottom:6px;letter-spacing:.06em}
  .payment-row{display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px dashed #cfd6e2;font-size:10.5pt}
  .payment-row em{font-style:normal;color:#666;font-size:9pt;margin-left:4px}
  .payment-row.grand{border-top:2px solid #1e6fd9;border-bottom:none;font-weight:800;font-size:11pt;margin-top:4px;padding-top:6px;color:#1e6fd9}
  .terms-fine{font-size:9pt;color:#333;margin:8px 0;font-style:italic;line-height:1.45}
  .terms-expiry{text-align:center;font-weight:800;color:#1e6fd9;margin:12px 0;font-size:11pt;letter-spacing:.02em}
  .initials-row{display:flex;align-items:center;gap:10px;margin-top:14px;padding-top:8px;border-top:1px dashed #cfd6e2}
  .initials-label{font-size:9pt;font-weight:700;color:#333;letter-spacing:.04em}
  .initials-circle{display:inline-block;width:36px;height:36px;border:2px solid #1e6fd9;border-radius:50%}
  .terms-pageno{position:relative;margin-top:24px;font-size:9pt;color:#666}
  .sig-block{display:grid;grid-template-columns:36px 1fr;gap:8px;margin:18px 0 6px}
  .sig-x{font-size:22pt;font-weight:800;color:#1e6fd9;line-height:1}
  .sig-role{grid-column:2;font-weight:700;color:#333;font-size:11pt;letter-spacing:.02em;margin-bottom:4px}
  .sig-lines{grid-column:2;font-size:9.5pt}
  .sig-line{display:flex;gap:8px;margin:6px 0;align-items:baseline}
  .sig-lbl{font-weight:700;min-width:52px;color:#333}
  .sig-fill{flex:1;border-bottom:1px solid #1e6fd9;height:18px}
  .sig-authority{color:#666;font-size:8.5pt;font-style:italic;margin-top:2px}
</style></head><body>
<div class="pf-quote">
  <div class="hdr">
    <div class="logo">${logoImg}</div>
    <div class="company">
      <div class="name">${escHtml(companyName)}</div>
      ${addressLine ? `<div>${escHtml(addressLine)}</div>` : ""}
      ${extraAddr}
      ${cphone}
      ${prepared}
      ${cemail}
    </div>
    <div class="meta">
      <div class="row"><span class="lbl">Date</span><span class="val">${escHtml(date)}</span></div>
      <div class="row"><span class="lbl">Valid Until</span><span class="val">${escHtml(validDays)}</span></div>
      <div class="row"><span class="lbl">Quote ID</span><span class="val">${escHtml(quoteId || "—")}</span></div>
    </div>
  </div>
  <div class="cust">
    <div>
      <div class="cust-head">Customer</div>
      <div class="name">${escHtml(custName)}</div>
      ${custAddr ? `<div>${escHtml(custAddr)}</div>` : ""}
      ${custCityLine}
    </div>
    <div style="align-self:end">
      ${custPhoneLine}
      ${custEmailLine}
    </div>
  </div>
  <table class="lines">
    <thead><tr>
      <th style="width:36px">Item</th>
      <th>Description</th>
      ${itemized ? '<th class="right" style="width:110px">Amount</th>' : ''}
    </tr></thead>
    <tbody>${lineRows || `<tr><td colspan="${itemized ? 3 : 2}" style="text-align:center;color:#888;padding:18px">No priced lines.</td></tr>`}</tbody>
  </table>
  <div class="totals">
    <div class="trow"><span class="lbl">Subtotal</span><span>${fmtMoney(baseWithProfit)}</span></div>
    <div class="trow"><span class="lbl">Tax<span class="tax-lbl">${escHtml(taxLabel)}</span></span><span>${fmtMoney(tax)}</span></div>
    <div class="trow grand"><span>Grand Total</span><span>${fmtMoney(grand)}</span></div>
  </div>
</div>
${termsPagesHtml}
${attachmentPages}
</body></html>`;

    // The popup may have been closed by the user while we were
    // crunching attachments — bail gracefully.
    if (popup.closed) return;
    popup.document.open();
    popup.document.write(html);
    popup.document.close();

    // Wait for the logo image (if any) to finish loading before
    // invoking print — otherwise the first print pass shows a
    // broken/missing logo.
    const triggerPrint = () => {
        try {
            popup.focus();
            popup.print();
        } catch {
            /* ignore */
        }
    };
    if (logoDataUrl) {
        const img = popup.document.querySelector(".logo img");
        if (img && !img.complete) {
            img.onload = triggerPrint;
            img.onerror = triggerPrint;
            setTimeout(triggerPrint, 1200); // safety net
        } else {
            setTimeout(triggerPrint, 400);
        }
    } else {
        setTimeout(triggerPrint, 400);
    }
}
